import { v4 as Uuid } from 'uuid'
import { assetDecodeManager } from '@/editor/media/AssetDecodeManager'
import { DEFAULT_DURATION, POSTPROCESSING_EFFECTS } from '@/etc/consts'
import { stripCanDrop, waitMs, blobFromUrl } from '@/etc/utils'
import { LOG } from '@/etc/log'
import { omit as _omit, pick as _pick } from 'lodash'
import supabase from '@/etc/supabase'
import { factory, tracksIndex } from './state'
import { getTransform2dCanonicalProp, isTransform2dEnvelopeProp } from '@/editor/smart/transform2d'
import {
  getQuickbarZoomCanonicalProp,
  isQuickbarZoomEnvelopeProp,
} from '@/editor/smart/quickbarSmart'
import {
  buildTransformAgentState,
  buildTransformAtFrame,
  resolveTransformFrameRange,
} from '@/editor/agents/transformState'
import { resolveTransformMotionPreview } from '@/editor/agents/transformSpeed'
import {
  isTextNormalizedEffectControlProp,
  normalizeTextEffectControl,
} from '@/editor/text/textEffectControls'
import GLOBAL_EFFECT_PRESETS from '@/editor/effects/globalEffectPresets'
import GLOBAL_TEXT_MOTION_PRESETS from '@/editor/text/globalTextMotionPresets'
import { normalizeTextMotionPreset } from '@/editor/text/textMotionPresets'
import { createEffectUserPreset } from '@/editor/effects/effectPresets'
import {
  VYBY_PRESETS_TABLE,
  VYBY_PRESET_KIND_EFFECT,
  VYBY_PRESET_KIND_NODE_WORKFLOW,
  VYBY_PRESET_KIND_TEXT_MOTION,
  normalizeVybyPresetRows,
  vybyPresetRowsToNodeWorkflowPresets,
  vybyPresetRowsToEffectPresets,
  vybyPresetRowsToTextMotionPresets,
} from '@/etc/vybyPresetCatalog'

const log = LOG.m('store::project')
const { VUE_APP_S3 } = process.env

const getStripRetimeAnchor = (store, tuuid, suuid) => {
  const anchor = store.state.tracks?.[tuuid]?.strips?.[suuid]?.retime?.anchor
  return anchor === 'front' ? 'front' : 'back'
}

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const finiteNum = (value, fallback = null) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const finitePositive = (value, fallback = null) => {
  const n = finiteNum(value, null)
  return n != null && n > 0 ? n : fallback
}

const finiteNonNegative = (value, fallback = null) => {
  const n = finiteNum(value, null)
  return n != null && n >= 0 ? n : fallback
}

const effectDropDebugEnabled = () =>
  typeof window !== 'undefined' && window.__VYBY_DEBUG_EFFECT_DROP === true

const debugEffectDrop = (label, payload = undefined) => {
  if (!effectDropDebugEnabled()) return
  if (typeof payload === 'undefined') {
    console.debug('[effect-drop]', label)
    return
  }
  console.debug('[effect-drop]', label, payload)
}

const toSecondsFromFrames = (frames, fps, fallback = null) => {
  const frameCount = finiteNonNegative(frames, null)
  const f = finitePositive(fps, null)
  if (frameCount == null || f == null) return fallback
  return frameCount / f
}

const firstNonEmptyString = (...values) => {
  for (const value of values) {
    if (typeof value !== 'string') continue
    const s = value.trim()
    if (s) return s
  }
  return ''
}

const mergeGlobalPresetLists = (fallback = [], cms = []) => {
  const out = []
  const index = new Map()
  ;[...(fallback || []), ...(cms || [])].forEach((preset) => {
    if (!preset) return
    const id = firstNonEmptyString(preset.id, preset.code)
    if (!id) return
    if (index.has(id)) {
      out.splice(index.get(id), 1, preset)
      return
    }
    index.set(id, out.length)
    out.push(preset)
  })
  return out
}

const cloneJson = (value) => {
  if (typeof value === 'undefined') return undefined
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    return undefined
  }
}

const DEFAULT_PROJECT_TITLE_PREFIX = 'Name your project '

const normalizeProjectBanner = (banner) => {
  if (!banner || typeof banner !== 'object') return null
  const id = firstNonEmptyString(banner.id)
  const path = firstNonEmptyString(banner.path)
  const mime = firstNonEmptyString(banner.mime)
  if (!id && !path) return null
  return { id, path, mime, explicit: banner.explicit === true }
}

const pickDefaultProjectBanner = (fileIds = [], fileRows = []) => {
  const rowsById = (fileRows || []).reduce((acc, row) => {
    if (row?.id) acc[row.id] = row
    return acc
  }, {})

  for (const id of fileIds || []) {
    const row = rowsById[id]
    const banner = normalizeProjectBanner(row)
    if (banner) return banner
  }
  return null
}

const getNextDefaultProjectTitle = async (store) => {
  const fallback = Object.keys(store.rootState.user?.projects || {}).length + 1
  if (!store.rootState.user?.uid) return `${DEFAULT_PROJECT_TITLE_PREFIX}${fallback}`

  const { data, error } = await supabase
    .from('projects')
    .select('name')
    .eq('owner', store.rootState.user.uid)

  if (error || !Array.isArray(data)) {
    return `${DEFAULT_PROJECT_TITLE_PREFIX}${fallback}`
  }

  const max = data.reduce((acc, project) => {
    const name = `${project?.name || ''}`.trim()
    const match = name.match(/^Name your project\s+(\d+)$/i)
    return match ? Math.max(acc, Number(match[1]) || 0) : acc
  }, 0)

  return `${DEFAULT_PROJECT_TITLE_PREFIX}${max + 1}`
}

const findStripLocation = (state, stripId) => {
  if (!stripId) return null
  const tuuid = Object.keys(state.tracks || {}).find(
    (id) => !!state.tracks?.[id]?.strips?.[stripId]
  )
  if (!tuuid) return null
  const strip = state.tracks?.[tuuid]?.strips?.[stripId] || null
  if (!strip) return null
  return { tuuid, strip }
}

const layerTrackIds = (state) =>
  Object.keys(state.tracks || {})
    .filter((tuuid) => state.tracks?.[tuuid]?.type === 'layer')
    .sort((a, b) => {
      const al = finiteNum(state.tracks?.[a]?.layer, 0)
      const bl = finiteNum(state.tracks?.[b]?.layer, 0)
      return al - bl
    })

const pickTextCreateTrackId = (state) => {
  const activeTrack = state.timeline?.activeTrack || null
  if (activeTrack && state.tracks?.[activeTrack]?.type === 'layer') return activeTrack
  const layers = layerTrackIds(state)
  return layers.length ? layers[0] : null
}

const pickInsertDepth = ({ state, tuuid, time, length }) => {
  const track = state.tracks?.[tuuid]
  if (!track) return 0
  const strips = Object.values(track.strips || {})
  let depth = Math.max(0, finiteNum(track.maxDepth, 0))
  for (let d = depth; d < depth + 20; d++) {
    const ok = !strips.some((s) => s && !stripCanDrop(d, time, length, s))
    if (ok) {
      depth = d
      break
    }
  }
  return depth
}

const extractTextFromTextAnimationOperations = (operations = []) => {
  for (const op of operations) {
    if (!isPlainObject(op)) continue
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || ''}`.trim().toLowerCase()
    if (target !== 'content' || action !== 'upsert') continue
    if (typeof op.payload === 'string') {
      const txt = op.payload.trim()
      if (txt) return txt
      continue
    }
    if (!isPlainObject(op.payload)) continue
    const fromFields = firstNonEmptyString(
      op.payload.text,
      op.payload.content,
      op.payload.value,
      op.payload.string,
      op.payload.label
    )
    if (fromFields) return fromFields
    if (Array.isArray(op.payload.lines)) {
      const lines = op.payload.lines
        .map((x) => (typeof x === 'string' ? x.trim() : ''))
        .filter((x) => !!x)
      if (lines.length) return lines.join('\n')
    }
  }
  return ''
}

const extractTextTimingFromOperations = (operations = [], fpsFallback = 24) => {
  let startSec = null
  let durationSec = null
  for (const op of operations) {
    if (!isPlainObject(op)) continue
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || ''}`.trim().toLowerCase()
    if (target !== 'timing' || action !== 'upsert' || !isPlainObject(op.payload)) continue
    const p = op.payload
    const opFps = finitePositive(p.fps, fpsFallback)
    if (startSec == null) {
      startSec = finiteNonNegative(
        p.startSec ?? p.startTimeSec ?? p.timeSec,
        toSecondsFromFrames(p.startFrame, opFps, null)
      )
    }
    if (durationSec == null) {
      durationSec = finitePositive(
        p.durationSec ?? p.lengthSec ?? p.spanSec,
        toSecondsFromFrames(
          p.durationFrames ?? p.lengthFrames ?? p.spanFrames,
          opFps,
          null
        )
      )
    }
  }
  return { startSec, durationSec }
}

const extractTextCreateStartSec = ({
  payload,
  timelinePlayheadSec = 0,
  projectFps = 24,
}) => {
  const create = isPlainObject(payload?.create) ? payload.create : {}
  const anchor = isPlainObject(create.anchor) ? create.anchor : {}
  const fps = finitePositive(create.fps, projectFps)
  const fromSec = finiteNonNegative(
    anchor.playheadSec ?? create.playheadSec,
    null
  )
  if (fromSec != null) return fromSec
  const fromFrame = finiteNonNegative(
    anchor.playheadFrame ?? create.playheadFrame,
    null
  )
  if (fromFrame != null) return toSecondsFromFrames(fromFrame, fps, timelinePlayheadSec)
  return finiteNonNegative(timelinePlayheadSec, 0)
}

const extractTextCreateDurationSec = ({ payload, operations = [], projectFps = 24 }) => {
  const create = isPlainObject(payload?.create) ? payload.create : {}
  const fps = finitePositive(create.fps, projectFps)
  const fromOps = extractTextTimingFromOperations(operations, fps).durationSec
  return finitePositive(
    create.durationSec ?? create.lengthSec ?? payload.durationSec ?? payload.lengthSec,
    finitePositive(
      toSecondsFromFrames(
        create.durationFrames ??
          create.lengthFrames ??
          payload.durationFrames ??
          payload.lengthFrames,
        fps,
        null
      ),
      finitePositive(fromOps, DEFAULT_DURATION)
    )
  )
}

const TEXT_ANIMATION_AGENT_WRITE_MODES = new Set([
  'merge_text_animation_ops',
  'replace_text_animation_ops_in_range',
  'replace_all_text_animation_ops',
  'append_text_animation_ops',
])

const TEXT_ANIMATION_OPERATION_TARGETS = new Set([
  'content',
  'timing',
  'style',
  'animation',
])

const TEXT_ANIMATION_STYLE_PROPS = new Set([
  'font.family',
  'font.size',
  'font.weight',
  'font.style',
  'font.color',
  'font.lineHeight',
  'font.letterSpacing',
  'font.align',
  'effect.outlineWidth',
  'effect.outlineColor',
  'effect.shadowColor',
  'effect.shadowBlur',
  'effect.shadowOffsetX',
  'effect.shadowOffsetY',
])

const TEXT_ANIMATION_STYLE_ALIASES = {
  fontFamily: 'font.family',
  family: 'font.family',
  fontSize: 'font.size',
  size: 'font.size',
  fontWeight: 'font.weight',
  weight: 'font.weight',
  fontStyle: 'font.style',
  style: 'font.style',
  color: 'font.color',
  fontColor: 'font.color',
  lineHeight: 'font.lineHeight',
  letterSpacing: 'font.letterSpacing',
  tracking: 'font.letterSpacing',
  align: 'font.align',
  alignment: 'font.align',
  outlineWidth: 'effect.outlineWidth',
  outlineColor: 'effect.outlineColor',
  shadowColor: 'effect.shadowColor',
  shadowBlur: 'effect.shadowBlur',
  shadowOffsetX: 'effect.shadowOffsetX',
  shadowOffsetY: 'effect.shadowOffsetY',
}

const TEXT_ANIMATION_NUMBER_STYLE_PROPS = new Set([
  'font.size',
  'font.weight',
  'font.lineHeight',
  'font.letterSpacing',
  'effect.outlineWidth',
  'effect.shadowBlur',
  'effect.shadowOffsetX',
  'effect.shadowOffsetY',
])

const TEXT_ANIMATION_SMART_PROPS = new Set([
  'position.x',
  'position.y',
  'scale.x',
  'scale.y',
  'rotation.z',
  'opacity',
])

const TEXT_ANIMATION_REGIONS = ['entry', 'sustain', 'exit']

const validateTextAnimationAgentOperations = (operations = []) => {
  if (!Array.isArray(operations)) return { ok: false, reason: 'invalid_operations' }
  for (const op of operations) {
    if (!isPlainObject(op)) return { ok: false, reason: 'invalid_operation' }
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || 'upsert'}`.trim().toLowerCase()
    if (!TEXT_ANIMATION_OPERATION_TARGETS.has(target)) {
      return { ok: false, reason: 'unsupported_text_animation_target', target }
    }
    if (!['upsert', 'remove'].includes(action)) {
      return { ok: false, reason: 'unsupported_text_animation_action', action }
    }
  }
  return { ok: true }
}

const resolveTextAnimationRangeSeconds = ({ payload, strip, fps }) => {
  const writeMode = `${payload?.writeMode || ''}`.trim()
  if (writeMode !== 'replace_text_animation_ops_in_range') {
    return { ok: true, range: null }
  }
  const range = payload?.range
  if (!isPlainObject(range)) {
    return { ok: false, reason: 'missing_text_animation_range', missing: ['range'] }
  }

  const clipFrameCount = Math.max(1, Math.round(finiteNum(strip?.length, 0) * fps))
  const startRaw = finiteNum(range.startFrame, null)
  const endRaw = finiteNum(range.endFrameExclusive, null)
  if (startRaw == null || endRaw == null) {
    return {
      ok: false,
      reason: 'invalid_text_animation_range',
      missing: ['range.startFrame', 'range.endFrameExclusive'],
    }
  }

  const startFrame = Math.max(0, Math.min(clipFrameCount - 1, Math.round(startRaw)))
  const endFrameExclusive = Math.max(
    startFrame + 1,
    Math.min(clipFrameCount, Math.round(endRaw))
  )

  return {
    ok: true,
    range: {
      startFrame,
      endFrameExclusive,
      startSec: startFrame / fps,
      endSec: endFrameExclusive / fps,
      spanSec: (endFrameExclusive - startFrame) / fps,
    },
  }
}

const normalizeTextStyleProp = (prop) => {
  const raw = `${prop || ''}`.trim()
  if (!raw) return ''
  return TEXT_ANIMATION_STYLE_ALIASES[raw] || raw
}

const normalizeTextStyleValue = (prop, value) => {
  if (isTextNormalizedEffectControlProp(prop)) {
    const num = finiteNum(value, null)
    return num == null ? undefined : normalizeTextEffectControl(prop, num)
  }
  if (TEXT_ANIMATION_NUMBER_STYLE_PROPS.has(prop)) {
    const num = finiteNum(value, null)
    return num == null ? undefined : num
  }
  if (typeof value === 'string') {
    const next = value.trim()
    return next ? next : undefined
  }
  return typeof value === 'number' || typeof value === 'boolean' ? value : undefined
}

const extractTextStyleOperations = (operations = []) => {
  const updates = {}
  for (const op of operations) {
    if (!isPlainObject(op)) continue
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || ''}`.trim().toLowerCase()
    if (target !== 'style' || action !== 'upsert' || !isPlainObject(op.payload)) continue
    const source = isPlainObject(op.payload.values)
      ? op.payload.values
      : isPlainObject(op.payload.style)
        ? op.payload.style
        : op.payload
    Object.entries(source).forEach(([key, value]) => {
      const prop = normalizeTextStyleProp(key)
      if (!TEXT_ANIMATION_STYLE_PROPS.has(prop)) return
      const next = normalizeTextStyleValue(prop, value)
      if (typeof next !== 'undefined') updates[prop] = next
    })
  }
  return updates
}

const textSmartDefaultValue = (prop, fallback = null) => {
  if (typeof fallback === 'number') return fallback
  if (prop === 'opacity') return 1
  if (prop.startsWith('scale.')) return 1
  return 0
}

const offsetToSeconds = (value, fps, fallback = 0) => {
  if (isPlainObject(value)) {
    const unit = `${value.unit || 'seconds'}`.trim().toLowerCase()
    const amount = finiteNum(value.value, null)
    if (amount == null) return fallback
    if (unit === 'frames') return amount / fps
    if (unit === 'beats') return amount * (60 / 120)
    return amount
  }
  return finiteNonNegative(value, fallback)
}

const beatsForTextRate = (micro = {}) => {
  const unit = `${micro.rateUnit || ''}`.trim().toLowerCase()
  const base = unit === 'sixteenth'
    ? 0.25
    : unit === 'eighth'
      ? 0.5
      : unit === 'half'
        ? 2
        : unit === 'whole'
          ? 4
          : 1
  const dotted = micro.dotted ? 3 / 2 : 1
  const triplet = micro.triplet ? 2 / 3 : 1
  return base * dotted * triplet
}

const normalizeTextMicro = (micro = {}) => {
  if (!isPlainObject(micro)) return null
  const next = {
    ...micro,
    shape: micro.shape || 'windowRelative',
    amp: finiteNum(micro.amp, 1),
    yoyo: typeof micro.yoyo === 'boolean' ? micro.yoyo : true,
    beatLocked: typeof micro.beatLocked === 'boolean' ? micro.beatLocked : true,
    beatLockScope: ['global', 'local'].includes(micro.beatLockScope)
      ? micro.beatLockScope
      : 'global',
    rateUnit: micro.rateUnit || 'quarter',
    dotted: !!micro.dotted,
    triplet: !!micro.triplet,
    phaseBeats: finiteNum(micro.phaseBeats, 0),
  }
  const beats = beatsForTextRate(next)
  next.every = isPlainObject(micro.every) ? micro.every : { unit: 'beats', value: beats }
  next.span = isPlainObject(micro.span) ? micro.span : { unit: 'beats', value: beats }
  if (isPlainObject(micro.window)) {
    next.window = {
      low: finiteNum(micro.window.low, 0),
      high: finiteNum(micro.window.high, 0),
    }
  } else if (Number.isFinite(Number(micro.radius))) {
    const radius = Math.abs(Number(micro.radius))
    next.window = { low: -radius, high: radius }
  }
  return next
}

const normalizeTextMicroByRegion = (microByRegion = {}) => {
  if (!isPlainObject(microByRegion)) return null
  return TEXT_ANIMATION_REGIONS.reduce((acc, region) => {
    const micro = normalizeTextMicro(microByRegion[region])
    if (micro) acc[region] = { ...micro, region }
    return acc
  }, {})
}

const normalizeTextSmartPart = (value, fallback) => {
  const source = isPlainObject(value) && Object.prototype.hasOwnProperty.call(value, 'value')
    ? value.value
    : value
  const next = finiteNum(source, null)
  return { value: next == null ? fallback : next }
}

const normalizeTextSmartGroup = ({
  prop,
  group,
  existing = null,
  baseValue = null,
  rangeSeconds = null,
  stripLength = 0,
  fps = 24,
} = {}) => {
  if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return null
  const fallback = textSmartDefaultValue(prop, baseValue)
  const current = isPlainObject(existing) ? existing : {}
  const next = {
    start: normalizeTextSmartPart(group.start ?? current.start, fallback),
    left: normalizeTextSmartPart(group.left ?? current.left, fallback),
    right: normalizeTextSmartPart(group.right ?? current.right, fallback),
    end: normalizeTextSmartPart(group.end ?? current.end, fallback),
    offsets: {
      entry: offsetToSeconds(
        group.offsets?.entry ?? group.entryOffset ?? group.entryOffsetSec,
        fps,
        rangeSeconds ? rangeSeconds.startSec : finiteNonNegative(current.offsets?.entry, 0)
      ),
      exit: offsetToSeconds(
        group.offsets?.exit ?? group.exitOffset ?? group.exitOffsetSec,
        fps,
        rangeSeconds
          ? Math.max(0, stripLength - rangeSeconds.endSec)
          : finiteNonNegative(current.offsets?.exit, 0)
      ),
    },
    links: isPlainObject(group.links)
      ? group.links
      : isPlainObject(current.links)
        ? current.links
        : { rightToLeft: false },
    entryEasing: group.entryEasing || current.entryEasing || 'Linear.None',
    sustainEasing: group.sustainEasing || current.sustainEasing || 'Linear.None',
    exitEasing: group.exitEasing || current.exitEasing || 'Linear.None',
  }
  const microByRegion = normalizeTextMicroByRegion({
    ...(current.microByRegion || {}),
    ...(group.microByRegion || {}),
  })
  if (microByRegion && Object.keys(microByRegion).length) {
    next.microByRegion = microByRegion
  }
  return next
}

const textMotionPresetById = (presetId, globalPresets = GLOBAL_TEXT_MOTION_PRESETS) => {
  const id = `${presetId || ''}`.trim()
  if (!id) return null
  return (globalPresets || []).find((preset) => preset?.id === id) || null
}

const textPresetPartValue = (part = {}, prop, scaleUnit = 'raw') => {
  if (prop === 'position.x') return part.position?.x
  if (prop === 'position.y') return part.position?.y
  if (prop === 'scale.x') {
    const value = finiteNum(part.scale?.x, null)
    if (value == null) return undefined
    return scaleUnit === 'percent' ? value / 100 : value
  }
  if (prop === 'scale.y') {
    const value = finiteNum(part.scale?.y, null)
    if (value == null) return undefined
    return scaleUnit === 'percent' ? value / 100 : value
  }
  if (prop === 'rotation.z') return part.rotation?.z
  return undefined
}

const textPresetGroupFromParts = ({ preset, prop }) => {
  if (!preset?.parts || !TEXT_ANIMATION_SMART_PROPS.has(prop)) return null
  const fallback = textSmartDefaultValue(prop)
  const group = {}
  ;['start', 'left', 'right', 'end'].forEach((partName) => {
    const value = finiteNum(
      textPresetPartValue(preset.parts?.[partName], prop, preset.scaleUnit),
      null
    )
    if (value == null) return
    if (Math.abs(value - fallback) > 1e-9) group[partName] = { value }
  })
  if (preset.offsets) {
    const entry = finiteNonNegative(preset.offsets.entry, 0)
    const exit = finiteNonNegative(preset.offsets.exit, 0)
    const offsets = {}
    if (entry > 1e-9) offsets.entry = entry
    if (exit > 1e-9) offsets.exit = exit
    if (Object.keys(offsets).length) group.offsets = offsets
  }
  return Object.keys(group).length ? group : null
}

const textPresetGroupDelta = (prop, group = {}) => {
  if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return null
  const fallback = textSmartDefaultValue(prop)
  const delta = {}
  ;['start', 'left', 'right', 'end'].forEach((partName) => {
    const value = finiteNum(group?.[partName]?.value ?? group?.[partName], null)
    if (value == null) return
    if (Math.abs(value - fallback) > 1e-9) delta[partName] = { value }
  })
  const entry = finiteNonNegative(group.offsets?.entry, 0)
  const exit = finiteNonNegative(group.offsets?.exit, 0)
  const offsets = {}
  if (entry > 1e-9) offsets.entry = entry
  if (exit > 1e-9) offsets.exit = exit
  if (Object.keys(offsets).length) delta.offsets = offsets
  if (group.entryEasing && entry > 1e-9) delta.entryEasing = group.entryEasing
  if (group.sustainEasing) delta.sustainEasing = group.sustainEasing
  if (group.exitEasing && exit > 1e-9) delta.exitEasing = group.exitEasing
  if (isPlainObject(group.links)) delta.links = cloneJson(group.links) || group.links
  if (isPlainObject(group.microByRegion)) {
    delta.microByRegion = cloneJson(group.microByRegion) || group.microByRegion
  }
  return Object.keys(delta).length ? delta : null
}

const textAnimationGroupsFromPreset = (payload = {}, globalTextMotionPresets = GLOBAL_TEXT_MOTION_PRESETS) => {
  const presetId = firstNonEmptyString(payload.presetId, payload.preset, payload.id)
  const preset = normalizeTextMotionPreset(
    textMotionPresetById(presetId, globalTextMotionPresets)
  )
  if (!preset) return null

  const groups = {}
  Object.entries(preset.props || {}).forEach(([prop, group]) => {
    if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return
    const delta = textPresetGroupDelta(prop, group)
    if (delta) groups[prop] = delta
  })

  ;(preset.supportedProps || []).forEach((prop) => {
    if (groups[prop]) return
    const group = textPresetGroupFromParts({ preset, prop })
    if (group) groups[prop] = group
  })

  Object.entries(preset.microByRegion || {}).forEach(([prop, microByRegion]) => {
    if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(microByRegion)) return
    groups[prop] = {
      ...(groups[prop] || {}),
      microByRegion: cloneJson(microByRegion) || microByRegion,
    }
  })

  return Object.keys(groups).length ? groups : null
}

const extractTextAnimationOperationSource = (
  payload = {},
  globalTextMotionPresets = GLOBAL_TEXT_MOTION_PRESETS
) => {
  if (!isPlainObject(payload)) return null
  const presetGroups = textAnimationGroupsFromPreset(payload, globalTextMotionPresets)
  if (presetGroups) return presetGroups
  if (isPlainObject(payload.props)) return payload.props
  if (isPlainObject(payload.smartKeys)) return payload.smartKeys
  if (isPlainObject(payload.groups)) return payload.groups
  if (payload.prop && isPlainObject(payload.group)) return { [payload.prop]: payload.group }
  return payload
}

const extractTextAnimationGroups = ({
  operations = [],
  strip,
  asset,
  writeMode,
  rangeSeconds = null,
  fps = 24,
  globalTextMotionPresets = GLOBAL_TEXT_MOTION_PRESETS,
} = {}) => {
  const groups = {}
  for (const op of operations) {
    if (!isPlainObject(op)) continue
    const target = `${op.target || ''}`.trim().toLowerCase()
    const action = `${op.action || ''}`.trim().toLowerCase()
    if (target !== 'animation' || action !== 'upsert' || !isPlainObject(op.payload)) continue
    const source = extractTextAnimationOperationSource(
      op.payload,
      globalTextMotionPresets
    )
    if (!isPlainObject(source)) continue
    Object.entries(source).forEach(([prop, group]) => {
      if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return
      const baseValues = {
        ...(asset?.defaultProps || {}),
        ...(strip?.animation?.values || {}),
      }
      const existing =
        writeMode === 'replace_all_text_animation_ops'
          ? null
          : groups[prop] || strip?.smartKeys?.[prop] || null
      const normalized = normalizeTextSmartGroup({
        prop,
        group,
        existing,
        baseValue: baseValues[prop],
        rangeSeconds,
        stripLength: finitePositive(strip?.length, DEFAULT_DURATION),
        fps,
      })
      if (normalized) groups[prop] = normalized
    })
  }
  return groups
}

const applyTextStyleUpdates = ({ strip, asset, updates }) => {
  if (!strip || !asset || !isPlainObject(updates) || !Object.keys(updates).length) {
    return false
  }
  if (!strip.animation || typeof strip.animation !== 'object') return false
  strip.animation.values = {
    ...(strip.animation.values || {}),
    ...updates,
  }
  if (Array.isArray(strip.animation.keyframes)) {
    strip.animation.keyframes = strip.animation.keyframes.map((kf) => {
      if (!kf || typeof kf !== 'object') return kf
      const next = {
        ...kf,
        props: { ...(kf.props || {}) },
        delay: { ...(kf.delay || {}) },
        easing: { ...(kf.easing || {}) },
      }
      Object.entries(updates).forEach(([prop, value]) => {
        if (typeof next.props[prop] === 'undefined' && prop !== 'font.size') return
        next.props[prop] = value
        if (typeof next.delay[prop] === 'undefined') next.delay[prop] = 0
        if (typeof next.easing[prop] === 'undefined') next.easing[prop] = 'Linear.None'
      })
      return next
    })
  }
  Object.entries(updates).forEach(([prop, value]) => {
    if (typeof asset.pset === 'function') asset.pset(prop, value)
  })
  if (typeof asset.emit === 'function') {
    asset.emit('updateAnimation', strip.animation)
    asset.emit('props', {
      ...(asset.lastProps || {}),
      ...updates,
    })
  }
  return true
}

const applyTextAnimationGroups = ({ store, tuuid, suuid, groups }) => {
  if (!store || !tuuid || !suuid || !isPlainObject(groups)) return 0
  let applied = 0
  Object.entries(groups).forEach(([prop, group]) => {
    if (!TEXT_ANIMATION_SMART_PROPS.has(prop) || !isPlainObject(group)) return
    store.commit('upsertSmartKeys', {
      tuuid,
      suuid,
      prop,
      group,
    })
    applied += 1
  })
  return applied
}

const normalizeEffectCode = (value) => {
  const code = `${value || ''}`.trim()
  return code || ''
}

const effectItemMath = (effectItem) => {
  if (typeof effectItem?.math === 'string' && effectItem.math.trim()) {
    return effectItem.math.trim()
  }
  if (
    isPlainObject(effectItem?.controls) &&
    typeof effectItem.controls.math === 'string' &&
    effectItem.controls.math.trim()
  ) {
    return effectItem.controls.math.trim()
  }
  return ''
}

const effectMatchesAgentItem = ({ effect = null, effectCode = null, math = null } = {}) => {
  if (!effect) return false
  const code = normalizeEffectCode(effectCode).toLowerCase()
  if (!code || `${effect.code || ''}`.trim().toLowerCase() !== code) return false
  const effectMath = `${math || ''}`.trim()
  if (code === 'postprocessing' && effectMath) {
    return `${effect.math || ''}`.trim() === effectMath
  }
  return true
}

const resolveStripEffectIds = ({ strip, effectId = null, effectCode = null, math = null }) => {
  if (!strip || !isPlainObject(strip.effects)) return []
  const explicitId = `${effectId || ''}`.trim()
  if (explicitId && strip.effects[explicitId]) return [explicitId]
  const code = normalizeEffectCode(effectCode)
  if (!code) return []
  return Object.entries(strip.effects)
    .filter(([, eff]) => effectMatchesAgentItem({ effect: eff, effectCode: code, math }))
    .map(([id]) => id)
}

const resolveStripEffectId = ({
  strip,
  effectId = null,
  effectCode = null,
  math = null,
}) => {
  const ids = resolveStripEffectIds({ strip, effectId, effectCode, math })
  return ids.length ? ids[0] : null
}

const EFFECTS_AGENT_WRITE_MODES = new Set([
  'merge_effect_controls',
  'replace_effect_controls_in_range',
  'replace_all_effect_controls',
  'append_effect_controls',
])

const EFFECTS_AGENT_CONTROL_GROUPS = ['values', 'auto', 'smart', 'mod']

const validateEffectsAgentItem = (effectItem) => {
  if (!isPlainObject(effectItem)) {
    return { ok: false, reason: 'invalid_effect_item' }
  }

  const action = `${effectItem.action || 'upsert'}`.trim().toLowerCase()
  if (!['upsert', 'remove'].includes(action)) {
    return { ok: false, reason: 'unsupported_effect_action', action }
  }

  const effectCode = normalizeEffectCode(effectItem.effectCode).toLowerCase()
  const explicitId = `${effectItem.effectId || ''}`.trim()
  if (!effectCode && !explicitId) {
    return { ok: false, reason: 'missing_effect_target' }
  }
  if (action === 'remove') return { ok: true }
  if (effectCode !== 'postprocessing') {
    return { ok: false, reason: 'unsupported_effect_code', effectCode }
  }

  const math = effectItemMath(effectItem)
  const style = math ? POSTPROCESSING_EFFECTS?.[math] : null
  if (!style) {
    return { ok: false, reason: 'unsupported_postprocessing_style', math }
  }

  const validParams = new Set(Object.keys(style.controls || {}))
  const controls = isPlainObject(effectItem.controls) ? effectItem.controls : {}
  for (const group of EFFECTS_AGENT_CONTROL_GROUPS) {
    if (!isPlainObject(controls[group])) continue
    const invalid = Object.keys(controls[group]).filter((key) => !validParams.has(key))
    if (invalid.length) {
      return {
        ok: false,
        reason: 'unsupported_effect_params',
        math,
        params: invalid,
      }
    }
  }

  return { ok: true }
}

const validateEffectsAgentItems = (effectItems = []) => {
  for (const effectItem of effectItems) {
    const validation = validateEffectsAgentItem(effectItem)
    if (!validation.ok) return validation
  }
  return { ok: true }
}

const resolveEffectsAgentRangeSeconds = ({ payload, strip, fps }) => {
  const writeMode = `${payload?.writeMode || ''}`.trim()
  if (writeMode !== 'replace_effect_controls_in_range') return { ok: true, range: null }
  const range = payload?.range
  if (!isPlainObject(range)) {
    return { ok: false, reason: 'missing_effect_range', missing: ['range'] }
  }

  const clipFrameCount = Math.max(1, Math.round(finiteNum(strip?.length, 0) * fps))
  const maxStart = Math.max(0, clipFrameCount - 1)
  const startRaw = finiteNum(range.startFrame, null)
  const endRaw = finiteNum(range.endFrameExclusive, null)
  if (startRaw == null || endRaw == null) {
    return { ok: false, reason: 'invalid_effect_range', missing: ['range.startFrame', 'range.endFrameExclusive'] }
  }

  const startFrame = Math.max(0, Math.min(maxStart, Math.round(startRaw)))
  const endFrameExclusive = Math.max(
    startFrame + 1,
    Math.min(clipFrameCount, Math.round(endRaw))
  )

  return {
    ok: true,
    range: {
      startFrame,
      endFrameExclusive,
      startSec: startFrame / fps,
      spanSec: (endFrameExclusive - startFrame) / fps,
    },
  }
}

const withEffectRangePatch = (patch, rangeSeconds = null) => {
  if (!rangeSeconds) return patch
  return {
    ...patch,
    time: rangeSeconds.startSec,
    span: rangeSeconds.spanSec,
  }
}

const removeMatchingEffectsForAgentItem = (store, { tuuid, suuid, strip, effectItem }) => {
  const ids = resolveStripEffectIds({
    strip,
    effectId: effectItem.effectId,
    effectCode: effectItem.effectCode,
    math: effectItemMath(effectItem),
  })
  ids.forEach((euuid) => {
    store.commit('removeEffect', { tuuid, suuid, euuid })
  })
  return ids.length
}

const buildEffectUpdatePatch = (effectItem) => {
  if (!isPlainObject(effectItem)) return {}
  const patch = {}
  if (typeof effectItem.math === 'string' && effectItem.math.trim()) {
    patch.math = effectItem.math.trim()
  }
  if (isPlainObject(effectItem.controls)) {
    const controls = { ...effectItem.controls }
    if (
      typeof controls.math === 'string' &&
      controls.math.trim() &&
      typeof patch.math === 'undefined'
    ) {
      patch.math = controls.math.trim()
    }
    if (typeof controls.math !== 'undefined') delete controls.math
    if (isPlainObject(controls.tweens)) {
      patch.tweens = controls.tweens
      delete controls.tweens
    }
    if (Object.keys(controls).length) patch.controls = controls
  }
  return patch
}

const stripBaseType = (strip) => `${strip?.asset?.type || ''}`.split('/')[0]

const isMediaEffectTargetType = (type) => ['video', 'image', 'text'].includes(`${type || ''}`)

const effectCodeRequiresAdjustmentHost = (value) => {
  const code = normalizeEffectCode(value).toLowerCase()
  return code === 'postprocessing' || code === 'blur'
}

const presetRequiresAdjustmentHost = (preset) => {
  const group = `${preset?.group || ''}`.trim().toLowerCase()
  return group === 'postprocessing' || group === 'blur'
}

const effectItemRequiresAdjustmentHost = (effectItem) => {
  if (!isPlainObject(effectItem)) return false
  const action = `${effectItem.action || 'upsert'}`.trim().toLowerCase()
  if (action === 'remove') return false
  return effectCodeRequiresAdjustmentHost(effectItem.effectCode)
}

const requestRequiresAdjustmentHost = ({
  effectItems = null,
  effectCode = null,
  preset = null,
} = {}) => {
  if (Array.isArray(effectItems) && effectItems.some((item) => effectItemRequiresAdjustmentHost(item))) {
    return true
  }
  return effectCodeRequiresAdjustmentHost(effectCode) || presetRequiresAdjustmentHost(preset)
}

const createAdjustmentHostOverStrip = (store, { suuid } = {}) => {
  if (!suuid) return null
  const tuuid = Object.keys(store.state.tracks).find(
    (id) => store.state.tracks[id]?.strips?.[suuid]
  )
  if (!tuuid) return null
  const clip = store.state.tracks[tuuid].strips[suuid]
  if (!clip) return null

  const t = Number(clip.start) || 0
  const len = Number(clip.length) || DEFAULT_DURATION

  // Keep the current UX invariant: adjustment host lands immediately behind the source clip.
  const tr = store.state.tracks[tuuid]
  const strips = Object.values(tr?.strips || {})
  const depth = (Number.isFinite(clip.depth) ? clip.depth : 0) + 1
  store.commit('_pushUndo', 'addAdjustmentLayerOverStrip')
  const blocked = strips.some((s) => s && !stripCanDrop(depth, t, len, s))
  debugEffectDrop('createAdjustmentHostOverStrip:start', {
    sourceStripId: suuid,
    trackId: tuuid,
    start: t,
    length: len,
    depth,
    blocked,
  })
  if (blocked) {
    store.commit('shiftTrackDepths', {
      uuid: tuuid,
      fromDepth: depth,
      delta: 1
    })
  }

  const asset = factory.createAdjustment()
  const stripId = Uuid()
  store.commit('addStrip', { auuid: asset.uuid, tuuid, time: t, depth, length: len, uuid: stripId })
  const createdStrip = store.state.tracks?.[tuuid]?.strips?.[stripId] || null
  if (!createdStrip) {
    debugEffectDrop('createAdjustmentHostOverStrip:missing-strip', {
      sourceStripId: suuid,
      trackId: tuuid,
      stripId,
    })
    return null
  }

  store.commit('activeTrack', tuuid)
  store.commit('activeStrip', stripId)
  store.commit('effectsView', true)
  store.commit('saved', false)
  store.commit('updateProjectLength')
  debugEffectDrop('createAdjustmentHostOverStrip:created', {
    sourceStripId: suuid,
    trackId: tuuid,
    stripId,
    depth: createdStrip.depth,
    start: createdStrip.start,
    length: createdStrip.length,
  })

  return {
    stripId,
    tuuid,
    length: len,
  }
}

const actions = {
    loadGlobalPresetCatalog: async (store, { force = false } = {}) => {
      if (store.state.globalPresetCatalogLoaded && !force) {
        return !store.state.globalPresetCatalogError
      }

      const { error, data } = await supabase
        .from(VYBY_PRESETS_TABLE)
        .select()
        .eq('enabled', true)
        .in('kind', [
          VYBY_PRESET_KIND_EFFECT,
          VYBY_PRESET_KIND_TEXT_MOTION,
          VYBY_PRESET_KIND_NODE_WORKFLOW,
        ])
        .order('sort_order', { ascending: true, nullsFirst: false })
        .order('name', { ascending: true })

      if (error) {
        log.warn('loadGlobalPresetCatalog fallback to static presets', error)
        store.commit('setGlobalPresetCatalogError', error.message || error)
        return false
      }

      const rows = normalizeVybyPresetRows(data || [])
      const effectPresets = mergeGlobalPresetLists(
        GLOBAL_EFFECT_PRESETS,
        vybyPresetRowsToEffectPresets(rows)
      )
      const textMotionPresets = mergeGlobalPresetLists(
        GLOBAL_TEXT_MOTION_PRESETS,
        vybyPresetRowsToTextMotionPresets(rows)
      )
      const nodeWorkflowPresets = vybyPresetRowsToNodeWorkflowPresets(rows)

      store.commit('setGlobalPresetCatalog', {
        rows,
        effectPresets,
        textMotionPresets,
        nodeWorkflowPresets,
      })
      return true
    },
    // Phase 1-2 (retime): wrappers
    toggleStripRetimeEnabled: (store, { tuuid, suuid, enabled, mode = null }) => {
      store.commit('toggleStripRetimeEnabled', { tuuid, suuid, enabled, mode })
    },
    addRetimePoint: (store, { tuuid, suuid, t, speed, affects }) => {
      store.commit('addRetimePoint', { tuuid, suuid, t, speed, affects })
      store.commit('retimeAutofitLengthLive', { tuuid, suuid, anchor: getStripRetimeAnchor(store, tuuid, suuid) })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    addRetimePointLive: (store, { tuuid, suuid, t, speed, affects }) => {
      store.commit('addRetimePointLive', { tuuid, suuid, t, speed, affects })
    },
    updateRetimePoint: (store, { tuuid, suuid, index, t, speed, affects }) => {
      store.commit('updateRetimePoint', { tuuid, suuid, index, t, speed, affects })
      store.commit('retimeAutofitLengthLive', { tuuid, suuid, anchor: getStripRetimeAnchor(store, tuuid, suuid) })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    updateRetimePointLive: (store, { tuuid, suuid, index, t, speed, affects }) => {
      store.commit('updateRetimePointLive', { tuuid, suuid, index, t, speed, affects })
    },
    removeRetimePoint: (store, { tuuid, suuid, index }) => {
      store.commit('removeRetimePoint', { tuuid, suuid, index })
      store.commit('retimeAutofitLengthLive', { tuuid, suuid, anchor: getStripRetimeAnchor(store, tuuid, suuid) })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    removeRetimePointLive: (store, { tuuid, suuid, index }) => {
      store.commit('removeRetimePointLive', { tuuid, suuid, index })
    },
    retimeAutofitLength: (store, { tuuid, suuid, anchor = 'back' }) => {
      store.commit('retimeAutofitLength', { tuuid, suuid, anchor })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    retimeAutofitLengthLive: (store, { tuuid, suuid, anchor = 'back' }) => {
      store.commit('retimeAutofitLengthLive', { tuuid, suuid, anchor })
      store.commit('updateProjectLength')
    },
    setRetimeAnchor: (store, { tuuid, suuid, anchor = 'back' }) => {
      store.commit('setRetimeAnchor', { tuuid, suuid, anchor })
      store.commit('saved', false)
    },
    setStripRetimeColor: (store, { tuuid, suuid, color }) => {
      store.commit('setStripRetimeColor', { tuuid, suuid, color })
      store.commit('saved', false)
    },
    init: async (store, project_id) => {
      await store.dispatch('loadGlobalPresetCatalog')

      if (!project_id && store.state.uuid) {
        project_id = store.state.uuid
      }

      const lastProject = project_id
      // log.info('init?', { init: store.state.initialized, lastProject, saved: store.state.saved, isNew: store.state.isNew, uuid: store.state.uuid })
      if (store.state.initialized) {
        if (
          (!lastProject && store.state.isNew) ||
          lastProject === store.state.uuid
        )
          return
        // log.warn('!isNew, need save?')
        factory.reset()
      }
      store.commit('initialized', !lastProject)
      // log.info('init', lastProject)
      factory.on('error', (err) => {
        factory.dbg.error('store::project::factory error', err)
        console.error(factory.dbg.error('store::project::factory error', err))
      })
      if (!lastProject) {
        await store.dispatch('newProject')
      } else {
        const loaded = await store.dispatch('loadProject', lastProject)
        if (!loaded) {
          console.log('project was not loaded, starting new')
          await store.dispatch('newProject')
        }
      }
    },
    resetProject: async (store) => {
      // song: defaultSong(),
      // environment: defaultEnv(),
      // picture: defaultPicture(),
      // generated: {},
      // library: {},
      // effects: fromStorage('vyby/effects', { ...VYBY_EFFECTS }),
      factory.reset()
      store.commit('reset')
    },
    newProject: async (store, options = {}) => {
      // await store.dispatch('resetProject')
      const { camera } = factory.defineScene()
      const uuid = Uuid()
      const title = await getNextDefaultProjectTitle(store)
      // console.log('new project', uuid)
      store.commit('uuid', uuid)
      store.commit('projectTitle', title)
      if (Number.isInteger(options?.slotIndex)) {
        store.commit('projectSetting', {
          code: 'projectSlotIndex',
          value: options.slotIndex,
        })
      }
      await store.dispatch(
        'user/updatePrivateProfile',
        { last_project: null },
        { root: true }
      )
      // Scene is now project-level settings (Phase 4). Keep only the Camera strip in the rig track.
      store.dispatch('addAssetToTrack', {
        auuid: camera.uuid,
        tuuid: tracksIndex.globalRigTrackUuid,
        time: 0,
        depth: 0,
      })
      store.commit('activeTrack', tracksIndex.trackUuid1)
      let stripCamera = null
      if (!store.state.tracks[tracksIndex.globalRigTrackUuid]) {
        log.warn(
          'No store.state.tracks[tracksIndex.globalRigTrackUuid] ',
          store.state.tracks[tracksIndex.globalRigTrackUuid]
        )
        return
      }
      Object.keys(store.state.tracks[tracksIndex.globalRigTrackUuid].strips).forEach(
        (sid) => {
          const strip = store.state.tracks[tracksIndex.globalRigTrackUuid].strips[sid]
          if (strip.asset.type === 'camera') stripCamera = sid
        }
      )
      store.commit('defineSceneCamera', { cameraStrip: stripCamera })
      store.commit('updateProjectLength')
      setTimeout(() => {
        store.commit('saved', true)
      }, 500)
      return uuid
    },
    loadProject: async (store, project_id) => {
      const { data, error } = await supabase
        .from('projects')
        .select()
        .eq('id', project_id)
        .maybeSingle()
      log.info('load project', data, error, project_id)
      if (!data || error) {
        log.warn('load project failed', data, error)
        return false
      }
      await store.dispatch('resetProject')
      const layerTrackUuid = (layer) =>
        Object.keys(data.structure.tracks).find(
          (tuuid) => data.structure.tracks[tuuid].layer === layer
        )
      const t0 = layerTrackUuid(0)
      const t1 = layerTrackUuid(1)
      const t2 = layerTrackUuid(2)
      const t3 = layerTrackUuid(3)
      const t4 = layerTrackUuid(4)
      const tg = Object.keys(data.structure.tracks).find(
        (tuuid) => data.structure.tracks[tuuid].type === 'global'
      )
      if (t0) tracksIndex.audioTrackUuid = t0
      if (t1) tracksIndex.trackUuid1 = t1
      if (t2) tracksIndex.trackUuid2 = t2
      if (t3) tracksIndex.trackUuid3 = t3
      if (t4) tracksIndex.trackUuid4 = t4
      if (tg) tracksIndex.globalRigTrackUuid = tg
      // log.log('trackIndex update', { tracksIndex, t0, t1, t2, t3, t4 })
      const getAssetStrip = (assetType) =>
        Object.keys(data.structure.tracks).reduce((acc, tuuid) => {
          if (acc) return acc
          const track = data.structure.tracks[tuuid]
          return Object.values(track.strips).reduce((acc2, strip) => {
            if (acc2) return acc2
            if (strip.asset && strip.asset.type === assetType) return strip
            return acc2
          }, null)
        }, null)
      const cameraStripObj = getAssetStrip('camera')
      if (cameraStripObj) {
        factory.defineScene(cameraStripObj.asset.uuid)
      } else {
        log.warn('Skip camera asset definition; camera strip not found in project structure')
      }

      // Ensure non-media assets exist in the runtime factory BEFORE tracks are committed.
      // (Adjustment layer assets have no corresponding file to load.)
      try {
        Object.keys(data.structure.tracks || {}).forEach((tuuid) => {
          const track = data.structure.tracks[tuuid]
          Object.values(track?.strips || {}).forEach((strip) => {
            if (!strip || !strip.asset) return
            factory.ensureFromObject(strip.asset)
          })
        })
      } catch (e) { /* noop */ }

      const projectFiles = await supabase
        .from('files')
        .select()
        .in('id', data.files)
      // log.log('load project files', projectFiles, data.files)
      if (projectFiles.error) {
        log.warn('project files info error', projectFiles.error)
        return false
      }
      if (projectFiles.data.length) {
        // disable
        for (let i = 0; i < projectFiles.data.length; i++) {
          const f = projectFiles.data[i]
          const fblob = await blobFromUrl(`${VUE_APP_S3}/${f.path}`)
          const fname = f.path.split('/').pop()
          console.log('filepath', `${VUE_APP_S3}/${f.path}`, fname)
          const ffile = new File([fblob], fname, { type: f.mime })
          ffile.uuid = f.id
          await store.dispatch('addMedia', ffile)
          // const fa = factory.fromFile(ffile)
          // console.log('load project file', fa)
        }
      }
      // log.log('library loaded')
      store.commit('uuid', data.id)
      store.commit('projectTitle', data.name)
      store.commit('songUpdate', data.intro.song)
      store.commit('envUpdate', data.intro.environment)
      store.commit('pictureUpdate', data.intro.picture)
      Object.keys(data.settings).forEach((code) => {
        if (code === 'nodeGraph') {
          store.commit('setNodeGraph', data.settings[code])
        } else {
          store.commit('projectSetting', { code, value: data.settings[code] })
        }
      })
      store.commit('redefineTracks', data.structure.tracks)
      store.commit('defineSceneCamera', { cameraStrip: cameraStripObj ? cameraStripObj.uuid : null })
      store.commit('updateProjectLength')
      // console.log('loaded!')
      setTimeout(() => {
        store.commit('saved', true)
      }, 500)
      return true
    },
    initProject: async (store, info) => {
      store.dispatch('addMedia', info.song.file)
      store.commit('projectTitle', `${info.song.title} by ${info.song.artist}`)
      store.commit('projectSetting', { code: 'bpm', value: info.song.bpm })
      // store.commit('songUpdate', info.song)
      // store.commit('envUpdate', info.environment)
      // store.commit('pictureUpdate', info.picture)
      store.dispatch('ai/calibrate', info, { root: true })
      store.dispatch('ai/genInit', null, { root: true })
    },
    saveProject: async (store) => {
      if (store.state.saved) return

      if (!store.state.uuid) {
        console.log('store.state.uuid!!! SKIP', store.state.uuid)
        return
      }
      // get all project files from files table to update list of files in project table
      const all_proj_files = await supabase
        .from('files')
        .select()
        .eq('project', store.state.uuid)
      //.maybeSingle()
      console.log('all_proj_files', all_proj_files.data)

      const all_proj_files_ids = all_proj_files.data
        ? Object.values(all_proj_files.data).map((item) => item.id)
        : []

      console.log('all_proj_files_ids', all_proj_files_ids)
      var existing = await supabase
        .from('projects')
        .select()
        .eq('id', store.state.uuid)
        .maybeSingle()
      if (existing.error) {
        console.warn('presave load existing error', existing.error)
      }
      const isNew = !existing.data // || !existing.data.files.length
      console.log('isNew existing', isNew, existing)

      if (existing.error) {
        console.warn('presave load existing error', existing.error)
      }
      const store_library_file_ids = Object.keys(store.state.library)
      // console.log('proj_files', store_library_file_ids, all_proj_files_ids)

      const files = store_library_file_ids

      if (isNew) {
        existing.data = { files: [] }
      }
      if (files.length) {
        existing.data.files = all_proj_files_ids
      }
      console.warn(files)

      const uploads = isNew
        ? files
        : files.filter((fid) => !all_proj_files_ids.includes(fid))
      console.log('Not exists uploads::', uploads)
      const saves = uploads.map((fid) => {
        const asset = factory.asset(fid)
        if (asset) {
          console.log('asset!', asset)

          console.log('store.state.library', store.state.library[fid])
          const fname = asset.meta.name
          const ext = fname.split('.').pop()
          console.log(
            'FILEEE',
            `${VUE_APP_S3}/${store.rootState.user.uid}/${store.state.uuid}/${fid}.${ext}`
          )
          return store.dispatch(
            'user/uploadFile',
            { file: asset.blob, name: `${store.state.uuid}/${fid}.${ext}` },
            { root: true }
          )
        } else {
          console.warn('Skip asset file ', fid)
        }
      })
      const uploadFileRecords = []
      const saves2 = uploads.map((fid) => {
        const asset = factory.asset(fid)
        if (asset) {
          const fname = asset.meta.name
          const ext = fname.split('.').pop()
          const updatedFile = {
            id: fid,
            owner: store.rootState.user.uid,
            project: store.state.uuid,
            path: `${store.rootState.user.uid}/${store.state.uuid}/${fid}.${ext}`,
            mime: asset.blob.type,
            size: asset.blob.size,
            onwall: true,
          }
          uploadFileRecords.push(updatedFile)
          console.log('upsert updatedFile', updatedFile)
          return supabase.from('files').upsert(updatedFile).select()
        } else {
          console.warn('Skip asset file ', fid)
        }
      })
      const projectFileRows = [
        ...((all_proj_files && all_proj_files.data) || []),
        ...uploadFileRecords,
      ]
      const projectSettings = { ...store.state.settings }
      const currentBanner = normalizeProjectBanner(projectSettings.projectBanner)
      projectSettings.projectBanner = currentBanner?.explicit
        ? currentBanner
        : pickDefaultProjectBanner(files, projectFileRows)
      const project = {
        name: store.state.settings.title,
        owner: store.rootState.user.uid,
        published: isNew ? false : existing.data.published,
        createdat: isNew ? new Date() : existing.data.createdat,
        updatedat: new Date(),
        intro: {
          song: {
            artist: store.state.song.artist,
            title: store.state.song.title,
            bpm: store.state.song.bpm,
            fileid: null,
          },
          environment: store.state.environment,
          picture: store.state.picture,
        },
        settings: projectSettings,
        structure: {
          tracks: store.state.tracks,
        },
        files,
      }
      await Promise.all(saves)
      await Promise.all(saves2)
      let saved = null
      if (isNew) {
        console.warn('Insert NEW proj files', project)
        project.id = store.state.uuid
        saved = await supabase.from('projects').upsert(project)
      } else {
        //console.log('store.state.uuid', store.state.uuid)
        saved = await supabase
          .from('projects')
          .update(project)
          .eq('id', store.state.uuid)
        console.warn('Update proj files', project)
      }
      /*
      await store.dispatch(
        'user/updatePrivateProfile',
        { last_project: store.state.uuid },
        { root: true }
      )
        */
      store.commit('saved', !saved.error)
      store.commit('redirect', true)
      console.log('SAVE PROJECT!', saved.error || 'no errors', project)
    },
    upsertPreset: (store, preset) => {
      const uuid = (preset || {}).uuid || Uuid()
      store.commit('upsertEffectPreset', { uuid, ...preset })
      return uuid
    },
    deletePreset: (store, preset) => {
      store.commit('delEffectPreset', preset || {})
    },
    addS3Media: async (store, { file, out }) => {
      console.log('adds3media', file, out)
      file.uuid = out.fid
      await store.dispatch('addMedia', file)
      const projectInfo = await supabase
        .from('projects')
        .select('files')
        .eq('id', store.state.uuid)
        .maybeSingle()
      if (!projectInfo.data || projectInfo.error) {
        console.warn('project not loaded, files not saved!', projectInfo.error)
        return
      }
      if (projectInfo.data.files.includes(out.fid)) {
        return
      }
      projectInfo.data.files.push(out.fid)
      await supabase
        .from('projects')
        .update({ files: [...projectInfo.data.files, out.fid] })
        .eq('id', store.state.uuid)
    },
    addMedia: async (store, file, uploadNew = false) => {
      //addMedia: async (store, { file, uploadNew }) => {
      //store.dispatch('addMediaToLibrary', file)
      if (!file) return
      const asset = factory.fromFile(file, file.uuid || null)
      //console.log('addMedia', { file, asset })
      if (!asset) return null
      const uuid = asset.uuid
      store.commit('libraryMediaNew', { uuid, type: file.type })
      asset.on('asset.error', (err) => {
        console.error('loading asset error', err)
      })
      asset.on('ready', () => {
        store.commit('libraryMediaData', { uuid, data: asset.meta })
        store.commit('saved', false)
        console.info('save added files with project')
        if (uploadNew) store.dispatch('saveProject')
        else log.warn('uploadNew not set or false. Skipping saveProject')
        try {
          assetDecodeManager.registerAsset({
            uuid,
            meta: asset.meta ? { ...asset.meta } : {},
            options: {
              decodeAudio: asset.type === 'audio' || asset.type === 'video',
              decodeVideo: asset.type === 'video',
            }
          })
        } catch (e) { console.warn('asset decode register meta failed', e) }
      })
      try {
        assetDecodeManager.registerAsset({
          uuid,
          blob: asset.blob || null,
          meta: asset.meta ? { ...asset.meta } : {},
          options: {
            decodeAudio: asset.type === 'audio' || asset.type === 'video',
            decodeVideo: asset.type === 'video',
          }
        })
      } catch (e) { console.warn('asset decode register failed', e) }
      return uuid
    },
    addText: async (store, { text, thumbnail }) => {
      if (!text) return
      const asset = await factory.fromText(text, thumbnail, {
        settings: store.state.settings,
        zeroFrame: store.state.timeline?.zeroFrame3D,
      })
      if (!asset) return null
      const uuid = asset.uuid
      store.commit('libraryMediaNew', { uuid, type: 'text/plain' })
      asset.on('ready', () => {
        store.commit('libraryMediaData', { uuid, data: asset.meta })
        store.commit('saved', false)
      })
      return uuid
    },
    editText: async (store, { auuid, text, thumbnail }) => {
      const asset = factory.asset(auuid)
      if (!asset) return
      asset.loadText(text, thumbnail)
      asset.on('ready', () => {
        store.commit('libraryMediaData', { uuid: auuid, data: asset.meta })
        store.commit('saved', false)
      })
    },
    autodrop: (store) => {
      const dest = store.getters.autodropTrack
      // get last time at track
      const track = store.state.tracks[dest]
      if (!track) {
        console.error('autodrop track not found!', { dest })
        return false
      }
      const len = store.state.dnd.meta.duration || DEFAULT_DURATION
      const strips = Object.values(track.strips)
      const canNotHere =
        strips.length &&
        strips.some((strip) => {
          // if (src.uuid === strip.uuid) return false
          return !stripCanDrop(
            track.depth,
            store.state.timeline.currentTime,
            len,
            strip
          )
        })
      /*
       */
      const lastTime = canNotHere
        ? Object.values(track.strips).reduce((acc, strip) => {
            return Math.max(acc, strip.start + strip.length)
          }, 0)
        : store.state.timeline.currentTime
      store.dispatch('addAssetToTrack', {
        auuid: store.state.dnd.uuid,
        tuuid: dest,
        time: lastTime,
      })
      store.commit('dnd', null)
    },
    addAssetToTrack: (store, { auuid, depth, tuuid, time }) => {
      // console.log('ADD ASSET TO TRACK', auuid, tuuid, time)
      const toAudio = tuuid === tracksIndex.audioTrackUuid
      const audioInit =
        toAudio &&
        !Object.keys(store.state.tracks[tracksIndex.audioTrackUuid].strips)
          .length
      // snapshot BEFORE change
      store.commit('_pushUndo', 'addAssetToTrack')
      store.commit('addStrip', { auuid, depth, tuuid, time })
      // Auto-extract sibling audio when adding a video clip (if enabled)
      try {
        const settings = store.state.settings || {}
        if (settings.autoExtractAudioFromVideo) {
          const asset = factory.asset(auuid)
          if (asset && asset.type === 'video') {
            const newSuuid = store.state.timeline.activeStrip
            if (newSuuid) store.dispatch('extractAudioFromVideoStrip', { suuid: newSuuid })
          }
        }
      } catch (e) { /* guard auto-extract */ }
      if (audioInit && store.state.settings.autoAdoptBPM)
        store.commit('updateProjectBPM', auuid)
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 3: add asset with lane insertion/shift ops (ultra-expand modal)
    addAssetToTrackWithLaneOp: (store, { auuid, depth, tuuid, time, laneOp = null }) => {
      const toAudio = tuuid === tracksIndex.audioTrackUuid
      const audioInit =
        toAudio &&
        !Object.keys(store.state.tracks[tracksIndex.audioTrackUuid].strips)
          .length

      // snapshot BEFORE change (single undo entry for lane shift + add)
      store.commit('_pushUndo', 'addAssetToTrackWithLaneOp')

      if (laneOp && typeof laneOp === 'object') {
        if (laneOp.type === 'insert' && Number.isFinite(Number(laneOp.atDepth))) {
          store.commit('shiftTrackDepths', { uuid: tuuid, fromDepth: Number(laneOp.atDepth), delta: 1 })
        } else if (laneOp.type === 'bottom') {
          // create a new bottom lane by shifting everything up by 1
          store.commit('shiftTrackDepths', { uuid: tuuid, fromDepth: 0, delta: 1 })
        }
      }

      store.commit('addStrip', { auuid, depth, tuuid, time })

      // Auto-extract sibling audio when adding a video clip (if enabled)
      try {
        const settings = store.state.settings || {}
        if (settings.autoExtractAudioFromVideo) {
          const asset = factory.asset(auuid)
          if (asset && asset.type === 'video') {
            const newSuuid = store.state.timeline.activeStrip
            if (newSuuid) store.dispatch('extractAudioFromVideoStrip', { suuid: newSuuid })
          }
        }
      } catch (e) { /* guard auto-extract */ }

      if (audioInit && store.state.settings.autoAdoptBPM)
        store.commit('updateProjectBPM', auuid)
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    moveStrip: (store, { suuid, tuuid, depth, tfuuid, time }) => {
      // snapshot BEFORE change
      store.commit('_pushUndo', 'moveStrip')
      store.commit('moveStrip', { suuid, tuuid, depth, tfuuid, time })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 3: move strip with lane insertion/shift ops (ultra-expand modal)
    moveStripWithLaneOp: (store, { suuid, tuuid, depth, tfuuid, time, laneOp = null }) => {
      // snapshot BEFORE change (single undo entry for lane shift + move)
      store.commit('_pushUndo', 'moveStripWithLaneOp')

      if (laneOp && typeof laneOp === 'object') {
        if (laneOp.type === 'insert' && Number.isFinite(Number(laneOp.atDepth))) {
          store.commit('shiftTrackDepths', { uuid: tuuid, fromDepth: Number(laneOp.atDepth), delta: 1 })
        } else if (laneOp.type === 'bottom') {
          store.commit('shiftTrackDepths', { uuid: tuuid, fromDepth: 0, delta: 1 })
        }
      }

      store.commit('moveStrip', { suuid, tuuid, depth, tfuuid, time })
      store.commit('ensureTrackMaxDepth', { uuid: tuuid, depth })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 2: Move an existing adjustment strip to immediate lane behind a target clip.
    // If the desired lane is occupied for that span, shift deeper lanes to keep "immediate behind" deterministic.
    moveAdjustmentLayerBehindStrip: (store, { suuid, targetStripId } = {}) => {
      if (!suuid || !targetStripId || suuid === targetStripId) return null
      const sourceLoc = findStripLocation(store.state, suuid)
      const targetLoc = findStripLocation(store.state, targetStripId)
      if (!sourceLoc || !targetLoc) return null
      const { tuuid: sourceTrackId, strip: sourceStrip } = sourceLoc
      const { tuuid: targetTrackId, strip: targetStrip } = targetLoc
      const sourceType = `${sourceStrip?.asset?.type || ''}`.split('/')[0]
      const targetType = `${targetStrip?.asset?.type || ''}`.split('/')[0]
      if (sourceType !== 'adjustment') return null
      if (!['video', 'image', 'text'].includes(targetType)) return null

      const start = Number(targetStrip.start) || 0
      const length = Number(targetStrip.length) || DEFAULT_DURATION
      const desiredDepth = Math.max(0, (Number.isFinite(Number(targetStrip.depth)) ? Number(targetStrip.depth) : 0) + 1)
      const targetTrack = store.state.tracks?.[targetTrackId]
      if (!targetTrack) return null

      store.commit('_pushUndo', 'moveAdjustmentLayerBehindStrip')
      const strips = Object.values(targetTrack.strips || {})
      const blocked = strips.some((s) => {
        if (!s || s.uuid === suuid) return false
        return !stripCanDrop(desiredDepth, start, length, s)
      })
      if (blocked) {
        store.commit('shiftTrackDepths', {
          uuid: targetTrackId,
          fromDepth: desiredDepth,
          delta: 1
        })
      }

      store.commit('moveStrip', {
        suuid,
        tuuid: targetTrackId,
        tfuuid: sourceTrackId,
        depth: desiredDepth,
        time: start,
      })
      // Use resize mutation directly to keep this flow as one undoable operation.
      store.commit('resizeActiveStrip', {
        start,
        length,
      })
      store.commit('ensureTrackMaxDepth', {
        uuid: targetTrackId,
        depth: desiredDepth
      })
      store.commit('saved', false)
      store.commit('updateProjectLength')
      return suuid
    },
    removeStrip: (store, { track, strip, force = false } = {}) => {
      if (!track || !strip) return
      // Camera strip is required (auto-managed). Block deletion unless explicitly forced.
      try {
        const cam = store.state.cameraStrip
        const isCamera = cam && strip === cam
        if (isCamera && !force) return
        const s = store.state.tracks?.[track]?.strips?.[strip]
        if (s?.asset?.type === 'camera' && !force) return
      } catch (e) { /* noop */ }
      // snapshot BEFORE change
      store.commit('_pushUndo', 'removeStrip')
      store.commit('removeStrip', { track, strip, force })
      store.commit('saved', false)
      store.commit('activeStrip', null)
    },
    playerSelect: (store, selection) => {
      if (!selection) {
        store.commit('activeStrip', null)
        // store.commit('activeTrack', null)
      } else {
        store.commit('activeTrack', selection.track)
        store.commit('activeStrip', selection.strip)
      }
    },
    // ------   REFACTORED   ---------------
    // active elements selection/deselection
    activeTrack: (store, uuid) => {
      if (store.state.timeline.activeTrack === uuid) return
      store.commit('activeTrack', uuid)
      store.commit('activeStrip', null)
      store.commit('activeKeyframe', null)
      store.commit('tool', [])
    },
    activeStrip: (store, uuid) => {
      if (store.state.timeline.activeStrip === uuid) return
      const trackUuid = Object.keys(store.state.tracks).find((track) =>
        Object.keys(store.state.tracks[track].strips).includes(uuid)
      )
      if (!trackUuid) return
      if (store.state.timeline.activeTrack !== trackUuid)
        store.commit('activeTrack', trackUuid)
      store.commit('activeStrip', uuid)
      store.commit('activeKeyframe', null)
      store.commit('tool', [])
    },
    activeKeyframe: (store, uuid) => {
      if (store.state.timeline.activeKeyframe === uuid) return
      store.commit('activeKeyframe', uuid)
      store.commit('tool', [])
      if (uuid) {
        const strip = store.getters.activeStrip
        const kf = store.getters.activeKeyframe
        if (strip) store.commit('currentTime', strip.start + kf.time)
      }
    },
    selectSmartKey: (store, { tuuid, suuid, prop, part }) => {
      const track = store.state.tracks[tuuid]
      if (!track) return
      const strip = track.strips[suuid]
      if (!strip) return
      // Phase 4: virtual transform lane (transform2d) uses canonical per-axis group for timing.
      const sk = strip.smartKeys || {}
      const timingProp = isTransform2dEnvelopeProp(prop)
        ? getTransform2dCanonicalProp()
        : isQuickbarZoomEnvelopeProp(prop)
          ? getQuickbarZoomCanonicalProp(prop)
          : prop
      const group = sk[timingProp] || { offsets: { entry: 0, exit: 0 } }
      const clamp = (v) => Math.max(0, Math.min(strip.length, v))
      const tStart = 0
      const tEnd = strip.length
      const tLeft = clamp(group.offsets?.entry || 0)
      const tRight = clamp(strip.length - (group.offsets?.exit || 0))
      const tRel = part === 'start' ? tStart : part === 'left' ? tLeft : part === 'right' ? tRight : tEnd
      const timeAbs = strip.start + tRel
      const curr = store.state.timeline.activeSmartKey
      // Toggle off if selecting the same smart key
      if (curr && curr.strip === suuid && curr.track === tuuid && curr.prop === prop && curr.part === part) {
        store.commit('activeSmartKey', null)
        return
      }
      // Ensure active selection points to this strip
      store.commit('activeTrack', tuuid)
      store.commit('activeStrip', suuid)
      // Set active smart key and move playhead
      store.commit('activeSmartKey', { track: tuuid, strip: suuid, prop, part, timeAbs })
      store.commit('currentTime', timeAbs)
    },
    activeKeyframeProps: (store, update) => {
      // console.log('akp', update)
      store.commit('activeKeyframeProps', update)
    },
    tool: (store, tool) => {
      const ntool = store.state.tool === tool ? [] : tool
      store.commit('tool', ntool)
    },
    toggleTool: (store, tool) => {
      if (store.state.tool.includes(tool)) store.commit('tool', [])
      else store.commit('tool', [tool])
    },
    resizeActiveStrip: (store, { start, length }) => {
      store.commit('resizeActiveStrip', { start, length })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 2: Regular trim action (no proportional keyframe scaling)
    trimActiveStrip: (store, { start, length }) => {
      store.commit('trimActiveStrip', { start, length })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    // Phase 2: Speed trim action; computes speed via mapping
    speedTrimActiveStrip: (store, { anchor = 'back', start, length }) => {
      store.commit('speedTrimActiveStrip', { anchor, start, length })
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    splitActiveStripAtPlayhead: (store) => {
      store.commit('splitActiveStripAtPlayhead')
      store.commit('saved', false)
      store.commit('updateProjectLength')
    },
    play: async (store) => {
      // console.log('play', store.state.play)
      // if (!store.state.tuned)
      // await store.dispatch('checkPlaySpeed')
      store.commit('play', true)
    },
    pause: (store) => {
      // console.log('pause', store.state.play)
      store.commit('play', false)
    },
    doRender: (store) => {
      store.commit('record', true)
    },
    stopRender: (store) => {
      store.commit('record', false)
    },
    launchEditorAiTool: async (store, { toolId = null, openPanel = true } = {}) => {
      const nextToolId = `${toolId || ''}`.trim()
      if (!nextToolId) return { ok: false, reason: 'missing_tool_id' }
      store.commit('aiTask', null)
      if (openPanel) {
        await store.dispatch('cmd/openPanel', { panel: 'vyby' }, { root: true })
      }
      store.commit('aiTool', nextToolId)
      return { ok: true, toolId: nextToolId }
    },
    launchEditorEffectSelection: async (
      store,
      { stripId = null, effectCode = null, preset = null, time = 0, openEffectsView = true } = {}
    ) => {
      const targetStripId = `${stripId || store.state.timeline.activeStrip || ''}`.trim()
      const nextEffectCode = `${effectCode || preset?.group || ''}`.trim()
      debugEffectDrop('launchEditorEffectSelection:input', {
        stripId: targetStripId || null,
        effectCode: nextEffectCode || null,
        presetGroup: preset?.group || null,
        time,
      })
      if (!targetStripId) {
        return { ok: false, reason: 'missing_target_strip' }
      }
      if (!nextEffectCode) {
        return { ok: false, reason: 'missing_effect_code' }
      }

      const hostTarget = await store.dispatch('resolveEffectHostTarget', {
        stripId: targetStripId,
        effectCode: nextEffectCode,
        preset: preset || null,
      })
      debugEffectDrop('launchEditorEffectSelection:host-target', hostTarget || null)
      if (!hostTarget?.ok) return hostTarget

      const finalTrackId = hostTarget.tuuid || null
      const finalStripId = hostTarget.targetStripId || targetStripId
      if (!finalTrackId || !finalStripId) {
        return { ok: false, reason: 'missing_resolved_effect_target' }
      }

      const requestedTime = Math.max(0, Number(time) || 0)
      const resolvedStrip = hostTarget.strip || null
      const effectTime = hostTarget.adjustmentHostCreated ? 0 : requestedTime
      const span = Math.max(0, Number(resolvedStrip?.length) || 0)

      await store.dispatch('addEffect', {
        type: preset ? 'effect-preset' : 'effect',
        effect: nextEffectCode,
        preset: preset || null,
        track: finalTrackId,
        strip: finalStripId,
        time: effectTime,
        span: span || undefined,
      })
      debugEffectDrop('launchEditorEffectSelection:add-effect', {
        trackId: finalTrackId,
        stripId: finalStripId,
        effectCode: nextEffectCode,
        effectTime,
        span: span || null,
      })

      store.commit('activeTrack', finalTrackId)
      store.commit('activeStrip', finalStripId)
      if (openEffectsView) store.commit('effectsView', true)

      return {
        ok: true,
        trackId: finalTrackId,
        stripId: finalStripId,
        adjustmentHostCreated: !!hostTarget.adjustmentHostCreated,
      }
    },
    addEffect: (store, data) => {
      const track = store.state.tracks[data.track]
      if (!track) return
      const strip = track.strips[data.strip]
      if (!strip) return
      // console.log('@addEffect', data)

      const effect = data.type === 'effect-preset'
        ? factory.createEffectPreset(data.preset, {
          bpm: store.state.settings.bpm,
          strip: data.strip,
          time: data.time,
          type: data.type,
          // pass through link fields when provided
          span: typeof data.span === 'number' ? data.span : undefined,
          linkedStrip: data.linkedStrip,
          scene: data.scene
        })
        : factory.createEffect(data, {
          bpm: store.state.settings.bpm,
          time: data.time,
          type: data.type,
          span: typeof data.span === 'number' ? data.span : undefined,
          linkedStrip: data.linkedStrip,
          scene: data.scene
        })

      if (!effect) return

      // console.log('ADD EFFECT', effect, effect.isPreset)
      store.commit('addEffect', {
        tuuid: data.track,
        suuid: data.strip,
        effect,
      })
      store.commit('saved', false)
    },
    addAdjustmentLayer: (store, { track = null, time = null, length = null } = {}) => {
      const tuuid = (track && store.state.tracks[track] && store.state.tracks[track].type === 'layer')
        ? track
        : (store.state.timeline.activeTrack && store.state.tracks[store.state.timeline.activeTrack]?.type === 'layer')
          ? store.state.timeline.activeTrack
          : tracksIndex.trackUuid1
      const t = Number.isFinite(time) ? time : (Number(store.state.timeline.currentTime) || 0)
      const len = Number.isFinite(length) && length > 0 ? length : DEFAULT_DURATION

      const asset = factory.createAdjustment()
      const tr = store.state.tracks[tuuid]
      const strips = Object.values(tr?.strips || {})
      // Find a free depth lane for this time span on the chosen track.
      let depth = Math.max(0, Number(tr?.maxDepth) || 0)
      for (let d = depth; d < depth + 20; d++) {
        const ok = !strips.some((s) => s && !stripCanDrop(d, t, len, s))
        if (ok) {
          depth = d
          break
        }
      }

      store.commit('_pushUndo', 'addAdjustmentLayer')
      store.commit('addStrip', { auuid: asset.uuid, tuuid, time: t, depth, length: len })
      store.commit('saved', false)
      store.commit('updateProjectLength')
      return store.state.timeline.activeStrip
    },
    addAdjustmentLayerOverStrip: async (store, { suuid, preset = null } = {}) => {
      const host = createAdjustmentHostOverStrip(store, { suuid })
      if (!host?.stripId) return null

      await store.dispatch('project/addEffect', {
        type: preset ? 'effect-preset' : 'effect',
        effect: 'postprocessing',
        preset,
        track: host.tuuid,
        strip: host.stripId,
        time: 0,
        span: host.length
      })

      return host.stripId
    },
    resolveEffectHostTarget: async (
      store,
      { stripId = null, effectItems = null, effectCode = null, preset = null } = {}
    ) => {
      const location = findStripLocation(store.state, stripId)
      if (!location) {
        return {
          ok: false,
          reason: 'target_strip_not_found',
          stripId: stripId || null,
        }
      }

      const needsAdjustmentHost =
        isMediaEffectTargetType(stripBaseType(location.strip)) &&
        requestRequiresAdjustmentHost({ effectItems, effectCode, preset })
      debugEffectDrop('resolveEffectHostTarget:input', {
        stripId,
        trackId: location.tuuid,
        stripType: stripBaseType(location.strip),
        effectCode,
        presetGroup: preset?.group || null,
        needsAdjustmentHost,
      })

      if (!needsAdjustmentHost) {
        return {
          ok: true,
          targetStripId: stripId,
          requestedTargetStripId: null,
          adjustmentHostCreated: false,
          tuuid: location.tuuid,
          strip: location.strip,
        }
      }

      const host = createAdjustmentHostOverStrip(store, { suuid: stripId })
      if (!host?.stripId) {
        return {
          ok: false,
          reason: 'adjustment_host_resolution_failed',
          stripId,
        }
      }

      const hostLocation = findStripLocation(store.state, host.stripId)
      if (!hostLocation) {
        return {
          ok: false,
          reason: 'adjustment_host_not_found',
          stripId: host.stripId,
        }
      }

      return {
        ok: true,
        targetStripId: host.stripId,
        requestedTargetStripId: stripId,
        adjustmentHostCreated: true,
        tuuid: hostLocation.tuuid,
        strip: hostLocation.strip,
      }
    },
    // linkPpeToClip removed in Phase 3 (adjustment layers replace scene-name routing)
    saveEffectPreset: (store, data) => {
      const preset = createEffectUserPreset({
        ..._pick(data, ['name', 'group', 'img']),
        id: data.preset.uuid,
        preset: _omit(data.preset, ['object', 'uuid']),
      })
      store.commit('upsertEffectPreset', preset)
      const { tuuid, suuid } = data
      if (!tuuid || !suuid) return
      const sourceEffect = factory.effect(data.preset.uuid) || {}
      const replacePreset = factory.createEffectPreset(preset, {
        bpm: store.state.settings.bpm,
        strip: suuid,
        time: sourceEffect.time || 0,
      })
      console.log('REPLACE', sourceEffect, replacePreset, data)
      if (sourceEffect && replacePreset) {
        store.commit('removeEffect', {
          suuid,
          euuid: sourceEffect.uuid,
        })
        store.commit('addEffect', {
          suuid,
          effect: replacePreset,
        })
      }
    },
    buildTransformAgentState: (store, { stripId = null, frameRange = null } = {}) =>
      buildTransformAgentState({
        projectState: store.state,
        stripId,
        frameRange,
      }),
    toggleClipRangeEdit: (store, { stripId = null, active = undefined } = {}) => {
      const targetStripId = stripId || store.state.timeline.activeStrip || null
      if (!targetStripId) return
      const currentActive =
        !!store.state.clipRangeEdit?.active &&
        store.state.clipRangeEdit?.stripId === targetStripId
      const nextActive =
        typeof active === 'boolean' ? active : !currentActive
      store.commit('clipRangeEdit', {
        active: nextActive,
        stripId: nextActive ? targetStripId : null,
      })
    },
    setActiveStripClipRange: (store, { range, enabled = true } = {}) => {
      const tuuid = store.state.timeline.activeTrack
      const suuid = store.state.timeline.activeStrip
      if (!tuuid || !suuid) return
      store.commit('setStripClipRange', { tuuid, suuid, range, enabled })
    },
    setActiveStripClipRangeLive: (store, { range, enabled = true } = {}) => {
      const tuuid = store.state.timeline.activeTrack
      const suuid = store.state.timeline.activeStrip
      if (!tuuid || !suuid) return
      store.commit('setStripClipRangeLive', { tuuid, suuid, range, enabled })
    },
    clearActiveStripClipRange: (store) => {
      const tuuid = store.state.timeline.activeTrack
      const suuid = store.state.timeline.activeStrip
      if (!tuuid || !suuid) return
      store.commit('clearStripClipRange', { tuuid, suuid })
    },
    openSubclipModal: (store, { stripId = null, source = null, payload = null } = {}) => {
      const targetStripId = stripId || store.state.timeline.activeStrip || null
      if (!targetStripId) return
      store.commit('subclipModal', {
        open: true,
        stripId: targetStripId,
        source,
        payload,
      })
    },
    closeSubclipModal: (store) => {
      store.commit('subclipModal', {
        open: false,
        stripId: null,
        source: null,
        payload: null,
      })
    },
    openTextModal: (store, { stripId = null, source = null } = {}) => {
      const targetStripId = stripId || store.state.timeline.activeStrip || null
      if (!targetStripId) return
      store.commit('textModal', {
        open: true,
        stripId: targetStripId,
        source,
      })
    },
    closeTextModal: (store) => {
      store.commit('textModal', {
        open: false,
        stripId: null,
        source: null,
      })
    },
    clearSubclipModalResult: (store) => {
      store.commit('subclipModalResult', null)
    },
    buildTransformAgentHandoff: (
      store,
      {
        task = {},
        policy = {},
        stripId = null,
        frameRange = null,
      } = {}
    ) => {
      const state = buildTransformAgentState({
        projectState: store.state,
        stripId,
        frameRange,
      })
      if (!state) return null

      return {
        agent: 'transform',
        task: {
          goal: task.goal || '',
          intent: task.intent || 'hold',
          speedProfile: task.speedProfile || 'medium',
          intensity: task.intensity || 'normal',
          startPolicy: task.startPolicy || 'current_frame',
          rangePolicy: task.rangePolicy || 'selected_range',
          overwriteIntent: task.overwriteIntent || 'merge',
          ...(task.notesFromPlanner
            ? { notesFromPlanner: task.notesFromPlanner }
            : {}),
        },
        state,
        policy: {
          framingPolicy: policy.framingPolicy || 'center_safe',
          allowBorderExposure: !!policy.allowBorderExposure,
          allowReplaceAllTransformKeys: !!policy.allowReplaceAllTransformKeys,
          preferMinimalKeyframes:
            typeof policy.preferMinimalKeyframes === 'boolean'
              ? policy.preferMinimalKeyframes
              : true,
          ...(typeof policy.allowCompoundMotion === 'boolean'
            ? { allowCompoundMotion: policy.allowCompoundMotion }
            : {}),
          ...(typeof policy.allowRotation === 'boolean'
            ? { allowRotation: policy.allowRotation }
            : {}),
          ...(Number.isFinite(Number(policy.safeScaleFloorPct))
            ? { safeScaleFloorPct: Number(policy.safeScaleFloorPct) }
            : {}),
          ...(Number.isFinite(Number(policy.maxKeyframes))
            ? { maxKeyframes: Number(policy.maxKeyframes) }
            : {}),
        },
      }
    },
    previewTransformAgentMotion: (
      store,
      {
        task = {},
        policy = {},
        stripId = null,
        frameRange = null,
      } = {}
    ) => {
      const handoff = actions.buildTransformAgentHandoff(store, {
        task,
        policy,
        stripId,
        frameRange,
      })
      if (!handoff) return null

      const effectiveRange = resolveTransformFrameRange({
        state: handoff.state,
        startPolicy: handoff.task.startPolicy,
        rangePolicy: handoff.task.rangePolicy,
      })
      if (!effectiveRange) return { ...handoff, effectiveRange: null, motion: null }

      const startFrameTransform = buildTransformAtFrame({
        projectState: store.state,
        stripId: handoff.state.selectedStripId,
        frame: effectiveRange.startFrame,
      })

      return {
        ...handoff,
        effectiveRange,
        startFrameTransform,
        motion: resolveTransformMotionPreview({
          task: handoff.task,
          frameSpan: effectiveRange.frameSpan,
          fps: store.state.settings.fps,
          currentTransform: startFrameTransform,
          transformMode: handoff.state.transformMode,
          policy: handoff.policy,
        }),
      }
    },
    applyTransformAgentResult: (
      store,
      { result = null, source = 'vyby-chat' } = {}
    ) => {
      const payload =
        isPlainObject(result) && typeof result.schema === 'string' ? result : null
      if (!payload) {
        return { applied: false, reason: 'invalid_payload' }
      }
      if (payload.schema !== 'transform.set_keyframes.v1') {
        return { applied: false, reason: 'unsupported_schema', schema: payload.schema }
      }

      const stripId =
        typeof payload.selectedStripId === 'string' && payload.selectedStripId
          ? payload.selectedStripId
          : null
      const tuuid = stripId
        ? Object.keys(store.state.tracks || {}).find(
          (id) => !!store.state.tracks?.[id]?.strips?.[stripId]
        )
        : null
      const strip = tuuid ? store.state.tracks?.[tuuid]?.strips?.[stripId] : null
      const stripAsset =
        strip && strip.asset?.uuid ? factory.asset(strip.asset.uuid) : null
      const runtimeRefreshEmitterFound = !!(
        strip &&
        stripAsset &&
        typeof stripAsset.emit === 'function'
      )

      store.commit('applyTransformAgentSetKeyframes', { payload, source })
      return {
        applied: true,
        schema: payload.schema,
        stripId: payload.selectedStripId || null,
        writeMode: payload.writeMode || null,
        keyframeCount: Array.isArray(payload.keyframes) ? payload.keyframes.length : 0,
        runtimeRefreshEmitterFound,
      }
    },
    applyTextAnimationAgentResult: async (
      store,
      { result = null, source = 'vyby-chat' } = {}
    ) => {
      const payload =
        isPlainObject(result) && typeof result.schema === 'string' ? result : null
      if (!payload) {
        return { applied: false, reason: 'invalid_payload', source }
      }
      if (payload.schema !== 'text_animation.apply.v1') {
        return { applied: false, reason: 'unsupported_schema', schema: payload.schema, source }
      }

      const mode = `${payload.mode || ''}`.trim().toLowerCase()
      const operations = Array.isArray(payload.operations) ? payload.operations : []
      const writeMode = `${payload.writeMode || 'merge_text_animation_ops'}`.trim()
      const fps = finitePositive(store.state.settings?.fps, 24)
      const playheadSec = finiteNonNegative(store.state.timeline?.currentTime, 0)
      const contentFromOps = extractTextFromTextAnimationOperations(operations)
      if (!TEXT_ANIMATION_AGENT_WRITE_MODES.has(writeMode)) {
        return {
          applied: false,
          reason: 'unsupported_text_animation_write_mode',
          writeMode,
          schema: payload.schema,
          source,
        }
      }
      const operationValidation = validateTextAnimationAgentOperations(operations)
      if (!operationValidation.ok) {
        return {
          applied: false,
          schema: payload.schema,
          source,
          ...operationValidation,
        }
      }

      if (mode === 'create') {
        const create = isPlainObject(payload.create) ? payload.create : {}
        const text = firstNonEmptyString(
          create.text,
          create.content,
          create.value,
          contentFromOps
        )
        if (!text) {
          return {
            applied: false,
            reason: 'missing_text_content',
            schema: payload.schema,
            source,
          }
        }

        const tuuid = pickTextCreateTrackId(store.state)
        if (!tuuid) {
          return {
            applied: false,
            reason: 'no_layer_track_available',
            schema: payload.schema,
            source,
          }
        }

        const startSec = extractTextCreateStartSec({
          payload,
          timelinePlayheadSec: playheadSec,
          projectFps: fps,
        })
        const durationSec = extractTextCreateDurationSec({
          payload,
          operations,
          projectFps: fps,
        })
        const rangeResolution = resolveTextAnimationRangeSeconds({
          payload,
          strip: { length: durationSec },
          fps,
        })
        if (!rangeResolution.ok) {
          return {
            applied: false,
            schema: payload.schema,
            source,
            ...rangeResolution,
          }
        }

        const auuid = await store.dispatch('addText', { text, thumbnail: null })
        if (!auuid) {
          return {
            applied: false,
            reason: 'text_asset_create_failed',
            schema: payload.schema,
            source,
          }
        }

        const depth = pickInsertDepth({
          state: store.state,
          tuuid,
          time: startSec,
          length: durationSec,
        })
        store.commit('addStrip', {
          auuid,
          tuuid,
          time: startSec,
          depth,
          length: durationSec,
        })
        const createdStripId = store.state.timeline.activeStrip || null
        store.commit('saved', false)
        store.commit('updateProjectLength')

        const createdParts = ['content', 'timing']
        const createdLocation = findStripLocation(store.state, createdStripId)
        if (createdLocation) {
          const createdAsset = factory.asset(auuid)
          const styleUpdates = extractTextStyleOperations(operations)
          if (applyTextStyleUpdates({
            strip: createdLocation.strip,
            asset: createdAsset,
            updates: styleUpdates,
          })) {
            createdParts.push('style')
          }
          const animationGroups = extractTextAnimationGroups({
            operations,
            strip: createdLocation.strip,
            asset: createdAsset,
            writeMode,
            rangeSeconds: rangeResolution.range,
            fps,
            globalTextMotionPresets: store.state.globalTextMotionPresets,
          })
          if (applyTextAnimationGroups({
            store,
            tuuid: createdLocation.tuuid,
            suuid: createdStripId,
            groups: animationGroups,
          })) {
            createdParts.push('animation')
          }
        }

        return {
          applied: true,
          schema: payload.schema,
          mode,
          stripId: createdStripId,
          trackId: tuuid,
          durationSec,
          source,
          appliedParts: createdParts,
          message: `Created text strip (${durationSec.toFixed(2)}s).`,
        }
      }

      if (mode !== 'edit') {
        return {
          applied: false,
          reason: 'unsupported_mode',
          mode: payload.mode || null,
          schema: payload.schema,
          source,
        }
      }

      const stripId =
        typeof payload.selectedStripId === 'string' && payload.selectedStripId.trim()
          ? payload.selectedStripId.trim()
          : null
      if (!stripId) {
        return {
          applied: false,
          reason: 'needs_context:selectedStripId',
          missing: ['selectedStripId'],
          schema: payload.schema,
          source,
        }
      }

      const location = findStripLocation(store.state, stripId)
      if (!location) {
        return {
          applied: false,
          reason: 'target_strip_not_found',
          stripId,
          schema: payload.schema,
          source,
        }
      }
      const { tuuid, strip } = location
      if (`${strip.asset?.type || ''}`.toLowerCase() !== 'text') {
        return {
          applied: false,
          reason: 'target_strip_not_text',
          stripId,
          schema: payload.schema,
          source,
        }
      }
      const rangeResolution = resolveTextAnimationRangeSeconds({ payload, strip, fps })
      if (!rangeResolution.ok) {
        return {
          applied: false,
          schema: payload.schema,
          source,
          ...rangeResolution,
        }
      }

      const appliedParts = []
      if (contentFromOps) {
        await store.dispatch('editText', {
          auuid: strip.asset.uuid,
          text: contentFromOps,
          thumbnail: null,
        })
        appliedParts.push('content')
      }

      const timing = extractTextTimingFromOperations(operations, fps)
      const nextStart = finiteNonNegative(timing.startSec, finiteNonNegative(strip.start, 0))
      const nextLength = finitePositive(timing.durationSec, finitePositive(strip.length, DEFAULT_DURATION))
      const startChanged = Math.abs(nextStart - finiteNonNegative(strip.start, 0)) > 1e-6
      const lengthChanged = Math.abs(nextLength - finitePositive(strip.length, DEFAULT_DURATION)) > 1e-6
      if (startChanged || lengthChanged) {
        store.commit('activeTrack', tuuid)
        store.commit('activeStrip', stripId)
        await store.dispatch('trimActiveStrip', { start: nextStart, length: nextLength })
        appliedParts.push('timing')
      }

      const textAsset = factory.asset(strip.asset.uuid)
      const styleUpdates = extractTextStyleOperations(operations)
      if (applyTextStyleUpdates({ strip, asset: textAsset, updates: styleUpdates })) {
        appliedParts.push('style')
      }

      const animationGroups = extractTextAnimationGroups({
        operations,
        strip,
        asset: textAsset,
        writeMode,
        rangeSeconds: rangeResolution.range,
        fps,
        globalTextMotionPresets: store.state.globalTextMotionPresets,
      })
      if (applyTextAnimationGroups({
        store,
        tuuid,
        suuid: stripId,
        groups: animationGroups,
      })) {
        appliedParts.push('animation')
      }

      if (!appliedParts.length) {
        return {
          applied: false,
          reason: 'no_supported_operations',
          stripId,
          schema: payload.schema,
          source,
        }
      }

      store.commit('saved', false)
      return {
        applied: true,
        schema: payload.schema,
        mode,
        stripId,
        source,
        appliedParts,
        message: `Updated text strip (${appliedParts.join(', ')}).`,
      }
    },
    applyEffectsAgentResult: async (
      store,
      { result = null, source = 'vyby-chat' } = {}
    ) => {
      const payload =
        isPlainObject(result) && typeof result.schema === 'string' ? result : null
      if (!payload) {
        return { applied: false, reason: 'invalid_payload', source }
      }
      if (payload.schema !== 'effects.apply.v1') {
        return { applied: false, reason: 'unsupported_schema', schema: payload.schema, source }
      }

      const stripId =
        typeof payload.selectedStripId === 'string' && payload.selectedStripId.trim()
          ? payload.selectedStripId.trim()
          : null
      if (!stripId) {
        return {
          applied: false,
          reason: 'needs_context:selectedStripId',
          missing: ['selectedStripId'],
          schema: payload.schema,
          source,
        }
      }

      const location = findStripLocation(store.state, stripId)
      if (!location) {
        return {
          applied: false,
          reason: 'target_strip_not_found',
          stripId,
          schema: payload.schema,
          source,
        }
      }
      let targetStripId = stripId
      let { tuuid, strip } = location
      const effectItems = Array.isArray(payload.effects) ? payload.effects : []
      const writeMode = `${payload.writeMode || 'merge_effect_controls'}`.trim()
      if (!effectItems.length) {
        return {
          applied: false,
          reason: 'no_effect_operations',
          stripId,
          schema: payload.schema,
          source,
        }
      }
      if (!EFFECTS_AGENT_WRITE_MODES.has(writeMode)) {
        return {
          applied: false,
          reason: 'unsupported_effect_write_mode',
          writeMode,
          schema: payload.schema,
          source,
        }
      }

      const validation = validateEffectsAgentItems(effectItems)
      if (!validation.ok) {
        return {
          applied: false,
          schema: payload.schema,
          source,
          ...validation,
        }
      }

      const fps = finitePositive(store.state.settings?.fps, 24)
      const rangeResolution = resolveEffectsAgentRangeSeconds({ payload, strip, fps })
      if (!rangeResolution.ok) {
        return {
          applied: false,
          schema: payload.schema,
          source,
          ...rangeResolution,
        }
      }
      const rangeSeconds = rangeResolution.range

      const hostTarget = await store.dispatch('resolveEffectHostTarget', {
        stripId,
        effectItems,
      })
      if (!hostTarget?.ok) {
        return {
          applied: false,
          reason: hostTarget?.reason || 'adjustment_host_resolution_failed',
          stripId: hostTarget?.stripId || targetStripId,
          schema: payload.schema,
          source,
        }
      }
      targetStripId = hostTarget.targetStripId || targetStripId
      tuuid = hostTarget.tuuid || tuuid
      strip = hostTarget.strip || strip
      const adjustmentHostCreated = !!hostTarget.adjustmentHostCreated
      const requestedTargetStripId = hostTarget.requestedTargetStripId || null

      let upserted = 0
      let removed = 0
      let skipped = 0

      for (const effectItem of effectItems) {
        if (!isPlainObject(effectItem)) {
          skipped++
          continue
        }
        const action = `${effectItem.action || 'upsert'}`.trim().toLowerCase()
        const effectCode = normalizeEffectCode(effectItem.effectCode)
        const shouldCreateNew =
          action === 'upsert' &&
          (writeMode === 'append_effect_controls' ||
            writeMode === 'replace_all_effect_controls')
        if (writeMode === 'replace_all_effect_controls' && action === 'upsert') {
          removed += removeMatchingEffectsForAgentItem(store, {
            tuuid,
            suuid: targetStripId,
            strip,
            effectItem,
          })
          strip = store.state.tracks?.[tuuid]?.strips?.[targetStripId] || strip
        }
        const resolvedEffectId = shouldCreateNew
          ? null
          : resolveStripEffectId({
              strip,
              effectId: effectItem.effectId,
              effectCode,
              math: effectItemMath(effectItem),
            })

        if (action === 'remove') {
          if (!resolvedEffectId) {
            skipped++
            continue
          }
          store.commit('removeEffect', {
            tuuid,
            suuid: targetStripId,
            euuid: resolvedEffectId,
          })
          removed++
          continue
        }

        const patch = withEffectRangePatch(buildEffectUpdatePatch(effectItem), rangeSeconds)
        if (resolvedEffectId) {
          if (Object.keys(patch).length) {
            store.commit('updateEffect', {
              tuuid,
              suuid: targetStripId,
              euuid: resolvedEffectId,
              data: patch,
            })
          }
          upserted++
          continue
        }

        if (!effectCode) {
          skipped++
          continue
        }

        await store.dispatch('addEffect', {
          type: 'effect',
          effect: effectCode,
          track: tuuid,
          strip: targetStripId,
          time: rangeSeconds ? rangeSeconds.startSec : 0,
          span: rangeSeconds ? rangeSeconds.spanSec : undefined,
        })
        const createdEffectId =
          typeof store.state.timeline.activeEffect === 'string'
            ? store.state.timeline.activeEffect
            : null
        if (!createdEffectId) {
          skipped++
          continue
        }

        if (Object.keys(patch).length) {
          store.commit('updateEffect', {
            tuuid,
            suuid: targetStripId,
            euuid: createdEffectId,
            data: patch,
          })
        }
        upserted++
      }

      const appliedCount = upserted + removed
      if (!appliedCount) {
        return {
          applied: false,
          reason: 'no_applicable_effect_changes',
          stripId: targetStripId,
          schema: payload.schema,
          source,
          skipped,
        }
      }

      store.commit('activeTrack', tuuid)
      store.commit('activeStrip', targetStripId)
      store.commit('effectsView', true)
      store.commit('saved', false)
      return {
        applied: true,
        schema: payload.schema,
        stripId: targetStripId,
        source,
        upserted,
        removed,
        skipped,
        adjustmentHostCreated,
        requestedTargetStripId,
        message: adjustmentHostCreated
          ? `Created adjustment host and applied effects (${upserted} upserted, ${removed} removed).`
          : `Applied effects changes (${upserted} upserted, ${removed} removed).`,
      }
    },
    // calculations
    checkPlaySpeed: async (store) => {
      store.commit('calculate', true)
      const ts = store.state.timeline.currentTime
      const testFrom = ts + 1 < store.state.timeline.length ? ts : ts - 1
      if (testFrom < 0) return 1
      store.commit('currentTime', testFrom)
      store.commit('tune', 1)
      const start = new Date().getTime()
      store.commit('play', true)
      await waitMs(1000)
      const end = new Date().getTime()
      store.commit('play', false)
      const testTo = store.state.timeline.currentTime
      store.commit('currentTime', ts)
      store.commit('calculate', false)
      const tune = (end - start) / ((testTo - testFrom) * 1000)
      store.commit('tune', tune)
      store.commit('tuned', true)
      console.log('TEST RESULTS', testTo - testFrom, end - start, tune)
    },
  }

export default actions
